import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  useToast,
  Select,
  Textarea,
  Switch,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  CheckboxGroup,
  Checkbox,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";

const GENRE_LIST = [
  "영화/드라마",
  "예능/코미디",
  "스포츠",
  "음식/요리",
  "썰/스토리",
  "개인방송/블로그",
  "IT/경제/재테크",
  "게임",
  "의학/과학/교육",
  "뉴스/정치",
  "뷰티",
  "비영리/사회운동",
  "동물",
  "엔터테인먼트",
  "여행/이벤트",
  "애니메이션",
  "음악/댄스",
  "자동차",
  "지식정보/쇼핑",
  "챌린지",
  "키즈",
  "패션",
];

const EditChannelModal = ({ isOpen, onClose, channel, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const toast = useToast();

  useEffect(() => {
    const fetchCompanies = async () => {
      const { data, error } = await supabase
        .from("content_company")
        .select("company_name");
      if (!error) {
        setCompanies(data.map((c) => c.company_name));
      }
    };

    if (isOpen) {
      fetchCompanies();
      if (channel) {
        // 기존 채널 데이터에서 회사 목록 처리
        let processedCompanyList = [];
        if (channel.company_list) {
          if (Array.isArray(channel.company_list)) {
            processedCompanyList = channel.company_list;
          } else if (typeof channel.company_list === "string") {
            // 문자열에서 불필요한 문자 제거 후 배열로 변환
            const cleanCompanyList = channel.company_list.replace(
              /[\[\]"]/g,
              ""
            );
            processedCompanyList = cleanCompanyList
              .split(",")
              .map((c) => c.trim())
              .filter((c) => c);
          }
        }

        setFormData({
          channel_title: channel.channel_title || "",
          channel_url: channel.channel_url || "",
          channel_id: channel.channel_id || "",
          genre: channel.genre || "",
          status: channel.status || "",
          subscriber_count: channel.subscriber_count || 0,
          views: channel.views || 0,
          user: channel.user || "",
          verify_code: channel.verify_code || "",
          type: channel.type || "",
          company_list: processedCompanyList,
          approved_yn: channel.approved_yn || false,
          approved_at: channel.approved_at || "",
        });
      }
    }
  }, [isOpen, channel]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" || type === "switch" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleNumberChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyChange = (selectedCompanies) => {
    setFormData((prev) => ({ ...prev, company_list: selectedCompanies }));
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      // 회사 목록을 쉼표로 구분된 문자열로 변환
      const companyListString =
        formData.company_list && formData.company_list.length > 0
          ? formData.company_list.join(", ")
          : "";

      const { error } = await supabase
        .from("channel")
        .update({
          ...formData,
          company_list: companyListString,
          modified_date: new Date().toISOString(),
        })
        .eq("id", channel.id);

      if (error) {
        toast({
          title: "수정 실패",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "채널 정보 수정 완료",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onSave();
        onClose();
      }
    } catch (error) {
      toast({
        title: "수정 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>채널 정보 수정</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel>채널명</FormLabel>
                <Input
                  name="channel_title"
                  value={formData.channel_title || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>채널 URL</FormLabel>
                <Input
                  name="channel_url"
                  value={formData.channel_url || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>채널 ID</FormLabel>
                <Input
                  name="channel_id"
                  value={formData.channel_id || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>사용자</FormLabel>
                <Input
                  name="user"
                  value={formData.user || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>장르</FormLabel>
                <Select
                  name="genre"
                  value={formData.genre || ""}
                  onChange={handleChange}
                >
                  <option value="">장르 선택</option>
                  {GENRE_LIST.map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>인증 코드</FormLabel>
                <Input
                  name="verify_code"
                  value={formData.verify_code || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>구독자 수</FormLabel>
                <NumberInput
                  value={formData.subscriber_count || 0}
                  onChange={(value) =>
                    handleNumberChange("subscriber_count", parseInt(value) || 0)
                  }
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>조회수</FormLabel>
                <NumberInput
                  value={formData.views || 0}
                  onChange={(value) =>
                    handleNumberChange("views", parseInt(value) || 0)
                  }
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>채널 타입</FormLabel>
                <Select
                  name="type"
                  value={formData.type || ""}
                  onChange={handleChange}
                >
                  <option value="">타입 선택</option>
                  <option value="A">브랜드 채널</option>
                  <option value="D">일반 채널</option>
                  <option value="O">오리지널 채널</option>
                  <option value="S">플플 쇼츠 채널</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>상태</FormLabel>
                <Select
                  name="status"
                  value={formData.status || ""}
                  onChange={handleChange}
                >
                  <option value="">상태 선택</option>
                  <option value="Approved">활성</option>
                  <option value="Pending">비활성</option>
                  <option value="Suspended">정지</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>회사 목록</FormLabel>
                <CheckboxGroup
                  value={formData.company_list || []}
                  onChange={handleCompanyChange}
                >
                  <Stack
                    spacing={[2, 4]}
                    direction={["column", "row"]}
                    wrap="wrap"
                  >
                    {companies.map((company) => (
                      <Checkbox key={company} value={company}>
                        {company}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </FormControl>
            </SimpleGrid>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="approved-switch" mb="0">
                승인됨
              </FormLabel>
              <Switch
                id="approved-switch"
                name="approved_yn"
                isChecked={formData.approved_yn || false}
                onChange={handleChange}
              />
            </FormControl>

            {formData.approved_yn && (
              <FormControl>
                <FormLabel>승인 날짜</FormLabel>
                <Input
                  name="approved_at"
                  type="datetime-local"
                  value={
                    formData.approved_at
                      ? new Date(formData.approved_at)
                          .toISOString()
                          .slice(0, 16)
                      : ""
                  }
                  onChange={handleChange}
                />
              </FormControl>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            취소
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={loading}>
            저장
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditChannelModal;
